import React, { useEffect, useRef, useState, useCallback } from 'react';

const Growm360Viewer = ({
    imagesCount = 36,
    imagesBaseUrl, // Keep for backward compatibility but prefer imagesUrls
    imagesFiletype = 'jpg',
    imageFilenamePrefix = '',
    imageQueryString = '',
    imagesUrls = [], // New prop: array of full image URLs
    imageInitialIndex = 0,
    imageInitialIndexTrigger,
    animateJumpToPOV = true,
    is180Pan = false,
    mouseDragSpeed = 1,
    autoplaySpeed = 15,
    reverse = false,
    autoplay = false,
    autoplayTarget = 0,
    onPointerClick,
    width = 500,
    height = 500,
    showRotationIconOnStartup = true,
    // notifyOnPointerDown?: (x: number, y: number) => void;
    // notifyOnPointerUp?: (x: number, y: number) => void;
    // notifyOnPointerMoved?: (x: number, y: number) => void;
    // shouldNotifyEvents?: boolean;
    }) => {

    const imagesRef = useRef([]);
    const [currentIndex, setCurrentIndex] = useState(imageInitialIndex);
    const currentIndexRef = useRef(currentIndex);
    const previousInitialIndex = useRef(imageInitialIndex);
    const dragStartX = useRef(0);
    const isDragging = useRef(false); // Track if dragging is active
    const [isAutoplaying, setIsAutoplaying] = useState(autoplay);
    const [isReversing, setIsReversing] = useState(false);
    const [isShowSpinIcon, setIsShowSpinIcon] = useState(false);
    const autoplayIntervalRef = useRef(null);
    const jumpToPOVIntervalRef = useRef(null);
    const clickStartTime = useRef(0);
    const maxAutoplaySpeed = 50; // 50 is an interval period of 20ms
    const minAutoplaySpeed = 5; // 5 is an period of 200ms
    const timeBeforeShowingSpinIcon = 2500;
    const standardImagesCount = 36;

    const updateImageVisibility = useCallback((index) => {
        imagesRef.current.forEach((img, i) => {
            if (img && img.style) { 
                img.style.display = i === index ? 'block' : 'none';
            }
        });
    }, []);

    useEffect(() => {
        currentIndexRef.current = currentIndex;
    }, [currentIndex]);

    useEffect(() => {
        if (imagesRef.current.length > 0) {
            updateImageVisibility(currentIndex);
        }
    }, [currentIndex, updateImageVisibility]);

    useEffect(() => {
        setIsAutoplaying(autoplay);
    }, [autoplay]);

    const handlePointerDown = (event) => {
        isDragging.current = true;
        dragStartX.current = event.clientX || event.touches[0].clientX;
        clickStartTime.current = Date.now();
        setIsAutoplaying(false); // Stop autoplay on interaction
    };

    const handlePointerMove = (event) => {
        if (!isDragging.current) return;

        const currentX = event.clientX || event.touches[0].clientX;
        const deltaX = (currentX - dragStartX.current) * mouseDragSpeed;

        if (Math.abs(deltaX) > 10) { // Adjust sensitivity here
        setIsShowSpinIcon(false);
        let newIndex = currentIndex;

        const direction = reverse ? -1 : 1; // Reverse direction if needed

        if (deltaX * direction < 0) {
            newIndex = is180Pan 
            ? Math.max(0, currentIndex - 1) // Clamp
            : (currentIndex - 1 + imagesCount) % imagesCount; // Loop
        } else {
            newIndex = is180Pan
            ? Math.min(imagesCount - 1, currentIndex + 1) // Clamp
            : (currentIndex + 1) % imagesCount; // Loop
        }

        setCurrentIndex(newIndex);
        dragStartX.current = currentX;
        }
    };

    const handlePointerUp = () => {
        const clickDuration = Date.now() - clickStartTime.current;

        if (clickDuration < 200) {
            // Trigger the click event if it was a short click
            if (typeof onPointerClick === "function") {
                onPointerClick();
            }
        }
        isDragging.current = false;
    };

    useEffect(() => {
        if (!isShowSpinIcon) {
          setTimeout(() => {
            setIsShowSpinIcon(true)
          }, timeBeforeShowingSpinIcon);
        }
      }, [])

    useEffect(() => {
        if (isAutoplaying) {
            clearInterval(autoplayIntervalRef.current);
            // Normalise speed to standardImagesCount
            const intervalFrequency = (imagesCount/standardImagesCount) * 
            Math.min(Math.max(autoplaySpeed, minAutoplaySpeed), maxAutoplaySpeed); 
            // Convert seconds to ms
            const interval =  1000 / intervalFrequency; 
            console.log('autoplay speed:', autoplaySpeed);
            //if is180Pan, play anim in reverse until we reach imageCount / 2 - 1
            autoplayIntervalRef.current = setInterval(() => {
                setCurrentIndex(prevIndex => {
                    let nextIndex;
                    // If is180Pan is true, we loop forward and then reverse
                    if (is180Pan) {
                        // Determine next index based on direction
                        if (isReversing) {
                        nextIndex = prevIndex - 1;
            
                        // If we reached autoplayTarget in reverse, stop autoplay
                        if (nextIndex < autoplayTarget) {
                            clearInterval(autoplayIntervalRef.current);
                            setIsAutoplaying(false);
                            setIsReversing(false);
                            return autoplayTarget;
                        }
                        } else {
                        nextIndex = prevIndex + 1;
            
                        // If we reached the end, switch to reverse
                        if (nextIndex >= imagesCount) {
                            setIsReversing(true);
                            return imagesCount - 1;
                        }
                        }
                    } 
                    else {
                        // Regular behavior for non-180Pan mode
                        nextIndex = prevIndex + 1;
                        if (nextIndex >= imagesCount) {
                        nextIndex = 0;
                        }
            
                        // Check if we've reached the target
                        if (nextIndex === autoplayTarget) {
                        clearInterval(autoplayIntervalRef.current);
                        setIsAutoplaying(false);
                        }
                    }
                    return nextIndex;
                });
            }, interval);
        }

        return () => clearInterval(autoplayIntervalRef.current);
    }, [isAutoplaying, is180Pan, isReversing]);

    // Animation Jump to POV
    useEffect(() => {
        // Only trigger animation if the initial index has actually changed
        if (
            animateJumpToPOV && 
            currentIndexRef.current !== imageInitialIndex
        ) {
            clearInterval(jumpToPOVIntervalRef.current);
    
            const getDirection = (from, to) => {
                return from < to ? 1 : -1;
            };


    
            const getShortestPath = (from, to) => {
                const forwardSteps = (to - from + imagesCount) % imagesCount;
                const backwardSteps = (from - to + imagesCount) % imagesCount;
                return forwardSteps <= backwardSteps
                    ? { steps: forwardSteps, direction: 1 }
                    : { steps: backwardSteps, direction: -1 };
            };
    
            const direction = is180Pan 
                ? getDirection(currentIndexRef.current, imageInitialIndex) 
                : getShortestPath(currentIndexRef.current, imageInitialIndex).direction;
    
            jumpToPOVIntervalRef.current = setInterval(() => {
                // Calculate next index
                const nextIndex = is180Pan ? currentIndexRef.current + direction : (currentIndexRef.current + direction + imagesCount) % imagesCount;
    
                if (is180Pan) {
                    const halfCount = imagesCount / 2;
    
                    // Direction-aware boundary check for 180 Pan
                    if (
                        (direction === 1 && nextIndex >= imagesCount) || // Prevent overflow on forward movement
                        (direction === -1 && nextIndex < 0)              // Prevent underflow on backward movement
                    ) {
                        clearInterval(jumpToPOVIntervalRef.current);
                        return;
                    }
                }
    
                // Update index
                setCurrentIndex(nextIndex);
                currentIndexRef.current = nextIndex;
    
                // Stop animation when reaching the target index
                if (nextIndex === imageInitialIndex) {
                    clearInterval(jumpToPOVIntervalRef.current);
                }
            }, 1000 / maxAutoplaySpeed);
        }
    
        // Save the current initial index as the previous one
        previousInitialIndex.current = imageInitialIndex;
    
        return () => clearInterval(jumpToPOVIntervalRef.current);
    }, [animateJumpToPOV, imageInitialIndexTrigger]);
    

    useEffect(() => {
        const container = imagesRef.current[0]?.parentNode;
        if (container) {
        container.addEventListener('mousedown', handlePointerDown);
        container.addEventListener('mousemove', handlePointerMove);
        container.addEventListener('mouseup', handlePointerUp);
        container.addEventListener('mouseleave', handlePointerUp);
        
        container.addEventListener('touchstart', handlePointerDown);
        container.addEventListener('touchmove', handlePointerMove);
        container.addEventListener('touchend', handlePointerUp);

        // Prevent default behavior like dragging the image itself
        container.addEventListener('dragstart', (e) => e.preventDefault());
        }

        return () => {
        if (container) {
            container.removeEventListener('mousedown', handlePointerDown);
            container.removeEventListener('mousemove', handlePointerMove);
            container.removeEventListener('mouseup', handlePointerUp);
            container.removeEventListener('mouseleave', handlePointerUp);

            container.removeEventListener('touchstart', handlePointerDown);
            container.removeEventListener('touchmove', handlePointerMove);
            container.removeEventListener('touchend', handlePointerUp);
        }
        };
    }, [handlePointerDown, handlePointerMove, handlePointerUp]);

    return (
        <div
        className="growm-360-viewer-container"
        style={{ width: `${width}px`, height: `${height}px`, overflow: 'hidden', cursor: 'grab', userSelect: 'none', WebkitUserDrag: 'none', }}
        >
            <img 
            className={`icon-360-spin`} 
            src={`${process.env.PUBLIC_URL}/360spinicon.png`} 
            //Center it 
            style={{
              display: isShowSpinIcon && showRotationIconOnStartup ? "block" : "none",
              position: 'absolute',
              top: '5%', 
              left: '50%',
              transform: 'translateX(-50%)',
              width: `${0.1 * width}px`,
              height: 'auto',
            }}
            ></img>
            {Array.from({ length: imagesCount }, (_, index) => {
                // Use imagesUrls if provided, otherwise fall back to constructing from parts
                const imageSrc = imagesUrls && imagesUrls.length > index
                    ? imagesUrls[index]
                    : `${imagesBaseUrl}${imageFilenamePrefix}${index + 1}.${imagesFiletype}${imageQueryString}`;
                
                return (
                    <img
                    key={index}
                    ref={(el) => (imagesRef.current[index] = el)}
                    src={imageSrc}
                    alt={`Rotating object`}
                    // alt={`360-view-${index + 1}`}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'none',
                        userSelect: 'none',
                        pointerEvents: 'none', // Disable pointer events on images
                    }}
                    />
                );
            })}
        </div>
    );
};

export default Growm360Viewer;